// VEIL-1 — **THE OWNER'S VEILED GROUP OVERSIGHT.** Her order, 2026-08-10.
//
// An owner-only moderation window over GROUP THREADS, for terms enforcement, bullying complaints and
// technical remedy. **Members never see it and never know.** Nothing renders in any group about the
// owner: no presence, no read receipt, no member-count change, no "Lala is in the group."
//
// THIS FILE IS THE LAW BOTH SIDES AGREE ON. It is imported by the SERVER (which opens the door and
// writes the ledger row) and by the DASHBOARD (which draws the window), and it imports only
// `./livingRoom.js` — which imports nothing — so `scripts/veil-groups.mjs` loads and RUNS it with no
// browser, no database and no network.
//
// ---------------------------------------------------------------------------
// >>> THE FOUR RULINGS THIS ROUND TURNS ON, WRITTEN WHERE THEY ARE ENFORCED. <<<
//
// 1. **THE DOOR IS THE EXISTING SUPPORT PASSCODE AND THERE IS NO SECOND ONE.** Her clause names
//    *"the same password already protecting the Ask Lala oversight backend"*, which is D3-3's
//    passcode and its signed, expiring, owner-bound ticket (`requireSupport`). A veil route is
//    `requireSupport`, never `requireOwner` — an owner sign-in ALONE may not wander into a group.
//    Minting a second passcode would be two answers to one question (R28/R29) on the one door whose
//    whole job is to be a second key.
//
// 2. **THE LEDGER IS THE EXISTING ONE, WITH ITS OWN `view` VALUE.** `ledgerSupportView` writes
//    `support.view` into `owner_actions`, which is APPEND-ONLY at the database and already rendered
//    in her audit trail — so *"visible in the dashboard's audit trail"* is satisfied by EXISTING
//    rather than by a second log nobody reads. **And it is AWAITED, never fire-and-forget**, which is
//    the opposite of the room's fault logger and is deliberate: her own sentence is *"oversight that
//    logs itself is defensible; silent oversight is not"*, so if the row cannot be written the view
//    does not happen.
//
// 3. **READ-ONLY IS A PROPERTY OF THE READER, NOT A DECISION ABOUT WHICH BUTTONS TO DRAW.** The
//    store's veil reader writes NOTHING to any `lr_*` table. That is not a tidiness preference:
//    `listMessages` stamps `last_read_at` on the viewer's own membership row, and the owner really is
//    a member of some groups (Elite 2026 has her in it), so opening a group through the member reader
//    would move her stamp and **raise a READ RECEIPT on a member's message** — her clause 3's exact
//    prohibition, arriving through the convenience of reusing a function.
//
// 4. **THE ACTIONS ARE THE EXISTING LADDER AND NO NEW POWER.** Her clause 5: an IT ticket from a
//    message, a flag into the moderation queue, and the existing warn/strike ladder from a sender's
//    name — and *"deletion and group removal stay on their existing owner paths."* So `warn` is
//    DERIVED from `MOD_ACTIONS` (R31) rather than re-typed, and `hide`/`delete`/`approve`/`decline`
//    are refused HERE, by the roster, rather than by nobody having drawn the button.
// ---------------------------------------------------------------------------

import { MOD_ACTIONS } from "./livingRoom.js";

// ---------------------------------------------------------------------------
// THE LEDGER VALUES. Two, because listing every group on the site and READING ONE are different acts
// with different weight (R18), and a trail that spelled them the same could not tell her which she
// did. They ride `ledgerSupportView`'s `view` field, which is why they are short machine keys: that
// field already carries `reports`, `transcript`, `usage` and `review` in exactly this shape.
// ---------------------------------------------------------------------------
export const VEIL_GROUP_VIEW = "veil-group";
export const VEIL_LEDGER_VIEWS = [VEIL_GROUP_VIEW];

// >>> **AND THE LIST IS DELIBERATELY NOT LEDGERED, WHICH THE WALKTHROUGH MEASURED RATHER THAN
// ARGUED (DECISIONS VEIL-1-6).** <<< The first build gave it its own `veil-groups` value on R18's
// reasoning that listing and reading are different acts. Then the rendered run reported **18 list
// rows against 2 openings**: the search box reloads as she types, so every keystroke wrote a line
// into a table with `DO INSTEAD NOTHING` on its update and delete. **An append-only trail she
// cannot prune, buried under her own typing, is a worse audit than none** — the two rows that name
// which group she read are the evidence, and they were one in ten.
//
// Her clause 4 is about an OPENING and says so in her own words: *"every veil opening writes an
// oversight log row — which owner seat, WHICH GROUP, when."* A list names no group and carries no
// member's words: titles, counts and creator room-names, all of which her Living Room seat can
// already see. So there is ONE ledger value, and it is the one her sentence asks for.
export const VEIL_LIST_LEDGERED = false;

// ---------------------------------------------------------------------------
// THE COPY. In the law rather than typed into the dashboard, so the rendered walkthrough asserts the
// SCREEN against the same strings her order was written against.
// ---------------------------------------------------------------------------
export const VEIL_LABEL = "Group oversight";
export const VEIL_INTRO = "Every group on the site. Opening one is written down.";
export const VEIL_READ_ONLY_NOTE = "Read-only. Nothing you do here is sent into the group, and nobody in it is told you opened it.";
export const VEIL_LEDGER_NOTE = "Every group you open is written to the action log: who, which group, when. It cannot be edited or deleted.";
export const VEIL_SEARCH_PLACEHOLDER = "Search groups";
export const VEIL_EMPTY = "There are no groups on the site yet.";
export const VEIL_NO_MATCH = "No group matches that.";
export const VEIL_WAITING = "Reading the groups...";
export const VEIL_FAILED = "The groups could not be read just now. Nothing has changed.";
export const VEIL_THREAD_FAILED = "That group could not be opened just now, and nothing was written down.";
export const VEIL_THREAD_EMPTY = "Nothing has been said in this group yet.";

// ---------------------------------------------------------------------------
// FOUR STATES, NEVER TWO (R18, and R64's cauldron ruling one room over). A read that has not answered
// HOLDS STILL: telling her the site has no groups because a query was slow is the one wrong thing to
// say on a screen she opens to answer a bullying complaint, and it is the same picture as the truth.
// `no-match` is separated from `empty` for the same reason it is in her closet — a filter that found
// nothing and a site with nothing on it are different facts.
// ---------------------------------------------------------------------------
export function veilListState({ items = null, failed = false, matches = 0 } = {}) {
  if (failed) return "failed";
  if (items === null) return "waiting";
  if (items.length === 0) return "empty";
  if (matches === 0) return "no-match";
  return "list";
}

// The thread window's own three. There is no `no-match` here: a group is opened whole or not at all.
export function veilThreadState({ thread = null, failed = false } = {}) {
  if (failed) return "failed";
  if (thread === null) return "waiting";
  if (!Array.isArray(thread.messages) || thread.messages.length === 0) return "empty";
  return "thread";
}

// ---------------------------------------------------------------------------
// HER CLAUSE 5, AS A ROSTER.
//
// `warn` is DERIVED from `MOD_ACTIONS` — one entry, carrying a `veil` flag beside its `queue` and
// `feed` ones, exactly as LR19 derived the room's two from the same list (R31). A fourth hand-typed
// moderation list is how a label, a verb and a confirmation sentence come to disagree.
//
// `ticket` and `flag` are DECLARED here because they are NOT moderation-ladder acts: one writes to
// her IT sheet and one raises a report into the queue, and neither touches the item's visibility.
// Putting them on `MOD_ACTIONS` would make `modActionValid` accept them at the owner action route,
// which has no branch for either — so they would fall through to the bare ledger line and answer
// `ok: true` having done NOTHING. A refusal is safe; a success that did not happen is not.
// ---------------------------------------------------------------------------
export const VEIL_MOD_ACTIONS = MOD_ACTIONS.filter((a) => a.veil);

export const VEIL_ACTIONS = [
  { key: "ticket", label: "File an IT ticket", note: "Puts it on today's list for CC. The member is not told from here." },
  { key: "flag", label: "Flag for review", note: "Raises it into your moderation queue with the gate's own read of it." },
  ...VEIL_MOD_ACTIONS.map((a) => ({ key: a.key, label: a.label, note: a.note })),
];
export const VEIL_ACTION_KEYS = VEIL_ACTIONS.map((a) => a.key);
export function veilActionValid(key) {
  return VEIL_ACTION_KEYS.includes(String(key || ""));
}

// **WHAT MAY NEVER BE REACHED FROM THIS WINDOW, NAMED RATHER THAN LEFT UNDRAWN (R17/R160).** Her
// sentence puts deletion and group removal on their existing owner paths; an absent button is not a
// law, and the next round that adds a control here would have nothing to fail against. Held as a
// DISJOINTNESS in the fixture, so a key added to `VEIL_ACTIONS` that appears below goes red.
export const VEIL_FORBIDDEN_ACTIONS = ["hide", "delete", "approve", "decline", "group-delete", "remove"];

// ---------------------------------------------------------------------------
// **WHICH DOOR AN ACTION POSTS TO, DECLARED ONCE.**
//
// `warn` goes to the owner action route that has run the ladder since LR1-5 — the strike, the link to
// the message that earned it, the ledger line and the report resolution are all already there, and a
// second implementation of a warn would be a second answer to what a strike is (R28/R29). `ticket`
// and `flag` are not on that roster and go to the veil's own door.
//
// An unrostered action resolves to `""` — no door at all — rather than to a plausible one, because a
// mis-routed moderation act is a write against the wrong mechanism (R25's absent-control argument on
// a destination instead of a button).
// ---------------------------------------------------------------------------
export const VEIL_ACTION_ROUTE = "/api/owner/living-room/veil-action";
export const MOD_ACTION_ROUTE = "/api/owner/living-room/action";
export const VEIL_LIST_ROUTE = "/api/owner/living-room/veil";
export const VEIL_THREAD_ROUTE = "/api/owner/living-room/veil-thread";

// **THE ONE COMPOSER FOR A GROUP'S ADDRESS**, beside the route it is built from, so the window never
// spells the path itself (R28/R29). It is HERE rather than in the component for a second, measured
// reason: R202's reach reader resolves a `${CONSTANT}` only where that constant is BOUND, and the
// component imports it — so a URL composed there is a live route the census reports as reached by
// nobody, which is the direction that costs a real finding somewhere else (R19/R165-2).
export const veilThreadHref = (id) => `${VEIL_THREAD_ROUTE}/${encodeURIComponent(String(id ?? ""))}`;

export function veilActionRoute(key) {
  const k = String(key || "");
  if (!veilActionValid(k)) return "";
  return VEIL_MOD_ACTIONS.some((a) => a.key === k) ? MOD_ACTION_ROUTE : VEIL_ACTION_ROUTE;
}

// The two the veil's own door really performs, derived from the roster rather than typed beside it,
// so the route and the window agree about which acts it handles.
export const VEIL_OWN_ACTIONS = VEIL_ACTION_KEYS.filter((k) => veilActionRoute(k) === VEIL_ACTION_ROUTE);

// ---------------------------------------------------------------------------
// HER CLAUSE 6 — **TEST GROUPS EXCLUDED, AND IT IS DECLARED WITH ITS REASON RATHER THAN APPLIED
// QUIETLY (R17/R160).**
//
// ITEM 10's precedent binds and it cuts the other way here: filtering a harness row is WRONG for a
// row that names money Stripe really took, and RIGHT for a count-like surface. This is a count-like
// surface — a list of the site's groups — so the filter is right, and it is the same paired question
// `searchMyGroups` already asks: a group is backstage when every member still in it is a test
// account. **Asked of the MEMBERS, because a thread carries no flag of its own.**
//
// A group with even ONE real member is a real group, whatever else is in it: her bullying complaint
// is about a person, and hiding the room that person is standing in is the failure this filter must
// never produce.
// ---------------------------------------------------------------------------
export const VEIL_TEST_EXCLUSION_REASON =
  "A group whose every remaining member is a test account is backstage, the same question the member search asks. One real member makes it a real group.";

export function veilGroupIsBackstage({ realMembers = 0 } = {}) {
  return (Number(realMembers) || 0) === 0;
}

// ---------------------------------------------------------------------------
// **WHAT THE WINDOW MAY DRAW ABOUT A MESSAGE.** The sender's name, the words, the time and the media
// — never a read receipt, never a "seen by", never anybody's last-online. Her clause 3 forbids the
// window from RENDERING those facts as much as it forbids producing them, and the payload simply does
// not carry them: the store's veil reader does not select them at all.
//
// Declared as a roster so the fixture can hold the payload to it in BOTH directions — a field that
// appears here and not in the payload is a promise nothing keeps, and a field in the payload that is
// not here is one nobody decided to show her.
// ---------------------------------------------------------------------------
export const VEIL_MESSAGE_FIELDS = ["id", "body", "author", "createdAt", "media", "deleted"];
export const VEIL_MESSAGE_FORBIDDEN_FIELDS = ["seen", "liked", "likes", "lastReadAt", "lastOnline"];
